import { useState } from 'react';
import {
  Anchor,
  Badge,
  Button,
  Divider,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  Textarea,
} from '@mantine/core';
import { Link } from 'react-router';
import type { ActionDetail } from '@isg/shared-types';
import { AKSIYON_NITELIK_ETIKETI, ISLERIM_HEDEF_HARITASI, aksiyonNiteligi } from '@isg/validation';
import { useAksiyonGecis } from '../api/queries';
import { AKSIYON_DURUM_ETIKET } from '../i18n/tr';
import { SatirEylemleri } from '../sozlesme';
import { AksiyonEkBolumu } from './aksiyon-ek-parts';
import { ANLAM } from '../theme';
import { tarihGoster } from '../lib/format';

/**
 * Aksiyon tablosu — iki ekranın (Bana atananlar + Aksiyonlar) ortak parçası
 * (F2·S2, Kapsam §22.3). DÜĞME GÖRÜNÜRLÜĞÜ ROLE GÖRE ama karar SUNUCUDA
 * (§2.16): ekran yalnız 403 alacak düğmeyi baştan göstermez —
 *
 * ✏️ **T2 (tur 2): DÜĞME GÖRÜNÜRLÜĞÜ ARTIK SUNUCUDAN — `aksiyon.yetkiler`.**
 * Bu dosya kapatma kuralını KENDİ TÜRETİYORDU (`ustMerci()` + `atayanUzman`
 * damgası okuması) ve sunucudaki kuralın İKİNCİ KOPYASIYDI. Kural 01.09'da
 * sunucuda değişti; kopya kalsaydı ekran eski cümleyi konuşmaya devam eder
 * ve kullanıcı **basılınca 403 veren bir düğme** görürdü (§2.40'ın en kötü
 * seçeneği). Kopya SİLİNDİ — ekran kural yazmaz, bayrağı okur.
 *
 *  - Tamamla-bildir → `yetkiler.tamamlayabilir`
 *  - Onayla-kapat  → `yetkiler.kapatabilir`
 *  - Geri aç       → `yetkiler.geriAcabilir`
 *  - Ekler: her satırda açılır (kanıt listesi/indirme herkese, yükleme
 *    sorumluya — `yetkiler.ekYukleyebilir`).
 */

/**
 * ⚠️ **İPTAL KAPALIYLA AYNI RENKTE DEĞİLDİR** (V3 — Tur 2½b): kapanış
 * `olumlu`dur (iş bitti), iptal bir vazgeçiştir → `bilgi` (nötr). Aynı
 * renge basmak, raporda ayrı sayılan iki gerçeği ekranda tek gerçeğe
 * indirirdi (kapanma oranı iptalleri de sayıyormuş gibi görünürdü).
 */
export function durumRozeti(durum: ActionDetail['durum']): React.JSX.Element {
  const renk =
    durum === 'open' || durum === 'cancelled'
      ? ANLAM.bilgi
      : durum === 'completed_reported'
        ? ANLAM.dikkat
        : ANLAM.olumlu;
  return (
    <Badge variant="light" color={renk} data-testid="aksiyon-durum">
      {AKSIYON_DURUM_ETIKET[durum]}
    </Badge>
  );
}

export function AksiyonTablosu({
  rows,
  testId,
}: {
  rows: ActionDetail[];
  testId: string;
}): React.JSX.Element {
  const gecis = useAksiyonGecis();
  const [bildirilecek, setBildirilecek] = useState<ActionDetail | null>(null);
  const [aciklama, setAciklama] = useState('');
  const [ekleriAcik, setEkleriAcik] = useState<string | null>(null);

  return (
    <>
      <Table striped highlightOnHover data-testid={testId}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Başlık</Table.Th>
            <Table.Th>İşyeri</Table.Th>
            <Table.Th>Kaynak</Table.Th>
            <Table.Th>Atayan</Table.Th>
            <Table.Th>Atanan</Table.Th>
            <Table.Th>Termin</Table.Th>
            <Table.Th>Durum</Table.Th>
            <Table.Th>İşlem</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((aksiyon) => {
            // ⚠️ T2: KURAL SUNUCUDAN. Burada durum/şapka/damga okuyarak
            // ikinci bir koşul YAZMA — `yetkiler` zaten durumu de içerir.
            const tamamlaGorunur = aksiyon.yetkiler.tamamlayabilir;
            const kapatGorunur = aksiyon.yetkiler.kapatabilir;
            const geriAcGorunur = aksiyon.yetkiler.geriAcabilir;
            // ⚠️ İŞARET SUNUCUDAN (`terminGecti` — madde 299; ekran termini
            // bugünle karşılaştırmaz). Kural tek okuyucudadır (`aksiyonGecikmisMi`):
            // rozet · liste süzgeci · bildirim taraması aynı cümleyi okur.
            // Burada `durum !== 'closed'` yazıyordu ve `cancelled` eklenince
            // İPTAL EDİLEN AKSİYON "Termin geçti" rozeti taşımaya başlamıştı.
            const gecikti = aksiyon.terminGecti;
            return (
              <Table.Tr key={aksiyon.id} data-testid="aksiyon-satiri">
                <Table.Td>
                  {/* T4 — kaydın KENDİ SAYFASINA bağlantı (orta tıklanabilir
                      olsun diye `Link`; satır eylemi değil, gezinti). */}
                  <Anchor
                    component={Link}
                    to={ISLERIM_HEDEF_HARITASI.aksiyon(aksiyon.id)}
                    size="sm"
                    data-testid="aksiyon-baslik-bagi"
                  >
                    {aksiyon.baslik}
                  </Anchor>
                  {/* A5 — DEVİR ROZETİ: bu kayıt sistemde yaşanmadı,
                      devredildi. Rozet NÖTR (`bilgi`): bir durum değil bir
                      KÖKEN bilgisidir. */}
                  {aksiyon.devirKaydi && <DevirRozeti />}
                  {aksiyon.tamamlanmaAciklamasi && (
                    <Text size="xs" c="dimmed">
                      Tamamlanma notu: {aksiyon.tamamlanmaAciklamasi}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{aksiyon.workplaceAd}</Table.Td>
                {/* T3 — KAYNAK HÜCRESİ KAYNAK KAYDINA BAĞLANIR. Kaynak tipi
                    adı tek başına *"Kaza araştırması"* der ama HANGİ kazayı
                    söylemez; bağ kurulmuşsa kaydın kendisine gidilir. */}
                <Table.Td>
                  <KaynakHucresi aksiyon={aksiyon} />
                </Table.Td>
                <Table.Td>{aksiyon.atayanEtiket}</Table.Td>
                <Table.Td>
                  <SorumluHucresi aksiyon={aksiyon} />
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Text size="sm">{tarihGoster(aksiyon.terminTarihi)}</Text>
                    {gecikti && (
                      <Badge variant="filled" color={ANLAM.gecikmis} data-testid="termin-gecikti">
                        Termin geçti
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>{durumRozeti(aksiyon.durum)}</Table.Td>
                <Table.Td>
                  {/* G12 — SATIRDA TEK BİRİNCİL EYLEM (P2). Kalanı ⋮ altında.
                      Birincil KULLANICIYA GÖRE değişir: sorumluya
                      "Tamamla-bildir", kapatma yetkilisine "Onayla-kapat"
                      (ikisi durum ekseninde birbirini dışlar — biri `open`,
                      diğeri `completed_reported`). "Ekler" HER ZAMAN menüdedir:
                      bir kanıt listesini açmak, satırın birincil işi değildir. */}
                  <SatirEylemleri
                    testId="aksiyon-eylemleri"
                    birincil={
                      tamamlaGorunur
                        ? {
                            etiket: 'Tamamla-bildir',
                            testId: 'tamamla-bildir',
                            onClick: () => {
                              setAciklama('');
                              setBildirilecek(aksiyon);
                            },
                          }
                        : kapatGorunur
                          ? {
                              etiket: 'Onayla-kapat',
                              testId: 'onayla-kapat',
                              pasif: gecis.isPending,
                              onClick: () =>
                                gecis.mutate({ id: aksiyon.id, gecis: 'approve-close' }),
                            }
                          : undefined
                    }
                    digerleri={[
                      {
                        etiket: 'Geri aç',
                        testId: 'geri-ac',
                        gizli: !geriAcGorunur,
                        pasif: gecis.isPending,
                        onClick: () => gecis.mutate({ id: aksiyon.id, gecis: 'reopen' }),
                      },
                      {
                        etiket: 'Ekler',
                        testId: 'ekleri-goster',
                        onClick: () =>
                          setEkleriAcik((mevcut) => (mevcut === aksiyon.id ? null : aksiyon.id)),
                      },
                    ]}
                  />
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      {/* ⚠️ §2.40 — SATIR EYLEMİNİN HATASI GÖRÜNÜR OLMAK ZORUNDA. "Onayla-kapat"
          ve "Geri aç" doğrudan mutasyon atar; hata mesajı yalnız
          "Tamamlandı bildir" modalında çiziliyordu, yani bu iki düğmenin
          403/409'u SESSİZCE YUTULUYORDU — kullanıcı bastığını ve hiçbir şey
          olmadığını görüyordu (sınıfın "sessizce düşer" yarısı). */}
      {gecis.isError && bildirilecek === null && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="aksiyon-eylem-hata">
          {gecis.error.message}
        </Text>
      )}

      <Modal
        opened={ekleriAcik !== null}
        onClose={() => setEkleriAcik(null)}
        title="Kanıt ekleri"
        size="lg"
      >
        {(() => {
          const secili = rows.find((satir) => satir.id === ekleriAcik);
          return secili ? <AksiyonEkBolumu aksiyon={secili} /> : null;
        })()}
      </Modal>

      <Modal
        opened={bildirilecek !== null}
        onClose={() => setBildirilecek(null)}
        title="Tamamlandı bildir"
      >
        <Stack>
          <Text size="sm">
            {bildirilecek?.baslik} — açıklama yazıp foto/PDF kanıtı ekleyebilirsiniz.
          </Text>
          <Textarea
            data-testid="tamamlanma-aciklamasi"
            placeholder="Yapılan işin kısa açıklaması"
            value={aciklama}
            onChange={(event) => setAciklama(event.currentTarget.value)}
          />
          {/* Kanıt yükleme bildirimden ÖNCE de yapılabilir: ek ayrı bir uçtur
              (aksiyon açıkken de yüklenir) — modal yalnız akışı bir araya
              getirir, iki isteği tek işleme bağlamaz. */}
          {bildirilecek && (
            <>
              <Divider />
              <AksiyonEkBolumu aksiyon={bildirilecek} />
            </>
          )}
          {gecis.isError && (
            <Text size="sm" c={ANLAM.engel} role="alert">
              {gecis.error.message}
            </Text>
          )}
          <Button
            data-testid="tamamla-bildir-onayla"
            loading={gecis.isPending}
            onClick={() => {
              if (!bildirilecek) return;
              gecis.mutate(
                {
                  id: bildirilecek.id,
                  gecis: 'report-done',
                  aciklama: aciklama.trim() === '' ? null : aciklama.trim(),
                },
                { onSuccess: () => setBildirilecek(null) },
              );
            }}
          >
            Bildir
          </Button>
        </Stack>
      </Modal>
    </>
  );
}

/**
 * A5 — DEVİR ROZETİ (2½-sonrası). Kaydın KÖKENİNİ söyler: *"bu iş bizim
 * sistemimizde yaşanmadı, sisteme geçerken devredildi."*
 *
 * ⚠️ **RENK NÖTR (`bilgi`) ve bu bir karardır:** devir bir DURUM değildir.
 * Uyarı renginde olsaydı kullanıcı bir sorun sanır, olumlu renkte olsaydı
 * kapanmış iş gibi okunurdu — oysa devredilen kayıt açık da olabilir.
 */
export function DevirRozeti(): React.JSX.Element {
  return (
    <Badge variant="light" color={ANLAM.bilgi} size="xs" data-testid="devir-rozeti">
      Devir kaydı
    </Badge>
  );
}

/**
 * A2 — SORUMLU HÜCRESİ. Hesabı olan sorumlu adıyla yazılır; devirde
 * BEYAN EDİLEN ad ise *"sistemde hesabı yok"* şerhiyle.
 *
 * ⚠️ **BEYAN, GERÇEK BİR KULLANICI GİBİ GÖSTERİLMEZ.** Ayrımı silmek,
 * *"bu iş kimin üzerinde"* sorusuna yanlış cevap vermek olurdu: o ada
 * bildirim gitmez, o kişi kaydı ekranında görmez.
 */
function SorumluHucresi({ aksiyon }: { aksiyon: ActionDetail }): React.JSX.Element {
  return (
    <Stack gap={2}>
      <Text size="sm">{aksiyon.atananEtiket}</Text>
      {aksiyon.devirSorumluMetni && (
        <Text size="xs" c="dimmed" data-testid="devir-sorumlu-serhi">
          Devir beyanı — sistemde hesabı yok
        </Text>
      )}
    </Stack>
  );
}

/**
 * T3 — LİSTEDEKİ KAYNAK HÜCRESİ. Kaynak TİPİ her zaman yazılır; tipli bir
 * bağ varsa kaydın kendisine bağlantı da eklenir.
 *
 * ⚠️ **SEBEP DÜĞÜMÜ BAĞLANTI DEĞİL, METİNDİR** — sebebin kendi sayfası
 * yoktur (alt kalemdir, P1); olay bağlantısı zaten aynı satırdadır.
 */
function KaynakHucresi({ aksiyon }: { aksiyon: ActionDetail }): React.JSX.Element {
  const hedef = aksiyon.incidentId
    ? { yol: `/olaylar/${aksiyon.incidentId}`, metin: aksiyon.incidentTakipNo }
    : aksiyon.nonconformityId
      ? {
          yol: `/uygunsuzluklar/${aksiyon.nonconformityId}`,
          metin: aksiyon.nonconformityTakipNo,
        }
      : aksiyon.trainingSessionId
        ? { yol: `/egitimler/${aksiyon.trainingSessionId}`, metin: aksiyon.trainingEtiket }
        : null;
  const ikincil =
    aksiyon.incidentCauseEtiket ?? aksiyon.riskItemFaaliyet ?? aksiyon.formSablonAd ?? null;
  // T1 — nitelik kaynaktan türetilir (uygunsuzluk → düzeltici · risk → önleyici);
  // veremeyen kaynakta satır yazılmaz.
  const nitelik = aksiyonNiteligi(aksiyon);
  return (
    <Stack gap={0}>
      {hedef?.metin ? (
        <Anchor component={Link} to={hedef.yol} size="sm" data-testid="aksiyon-kaynak-bagi">
          {aksiyon.kaynakTipiAd} · {hedef.metin}
        </Anchor>
      ) : (
        <Text size="sm">{aksiyon.kaynakTipiAd}</Text>
      )}
      {ikincil && (
        <Text size="xs" c="dimmed" data-testid="aksiyon-kaynak-ikincil">
          {ikincil}
        </Text>
      )}
      {nitelik && (
        <Text size="xs" c="dimmed" data-testid="aksiyon-nitelik">
          {AKSIYON_NITELIK_ETIKETI[nitelik]}
        </Text>
      )}
    </Stack>
  );
}
